// src/components/CommonConfirmModal.tsx
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  useDisclosure,
  Textarea,
  Radio,
  RadioGroup,
} from '@heroui/react';
import { useState } from 'react';

import { AiOutlineDelete } from 'react-icons/ai';
import { MdQuestionMark, MdWarning, MdInfo, MdDangerous } from 'react-icons/md';
import CustomButton from '../../CustomButton';
import CustomTextArea from './CustomTextArea';

interface CommonConfirmModalProps {
  // Button props
  isIcon?: boolean;
  buttonLabel?: string;
  buttonColor?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger';
  buttonVariant?:
    | 'solid'
    | 'bordered'
    | 'light'
    | 'flat'
    | 'faded'
    | 'shadow'
    | 'ghost';
  buttonIcon?: React.ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;

  // Modal props
  title?: string;
  description?: string;
  iconType?: 'warning' | 'danger' | 'info' | 'question';

  // Action buttons
  confirmBtnText?: string;
  cancelBtnText?: string;
  confirmBtnColor?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger';

  // Step 1 Broadcast Provider Selectors
  providerSelectorOptions?: { value: string; label: string }[];
  selectedProviderValue?: string;
  onProviderChange?: (value: string) => void;

  // Step 2 Reason input
  requireReason?: boolean;
  reasonLabel?: string;
  reasonDescription?: string;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
  reasonMinLength?: number;
  reasonMaxLength?: number;
  reasonOptions?: { value: string; label: string }[];
  otherOptionValue?: string;

  // Data to pass back
  data?: any;

  // Callbacks
  onConfirm?: (reason?: string, data?: any) => void | Promise<void>;
  onCancel?: () => void;
  triggerClassName?: string;
}

const CommonConfirmModal = ({
  isIcon = false,
  buttonLabel = 'Confirm',
  buttonColor = 'primary',
  buttonVariant = 'solid',
  buttonIcon,
  isLoading = false,
  isDisabled = false,

  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  iconType = 'question',

  confirmBtnText = 'Confirm',
  cancelBtnText = 'Cancel',
  confirmBtnColor = 'primary',

  providerSelectorOptions,
  selectedProviderValue = 'ALL',
  onProviderChange,

  requireReason = false,
  reasonLabel = 'Reason (Optional)',
  reasonDescription = '',
  reasonPlaceholder = 'Please provide a reason for this action...',
  reasonRequired = false,
  reasonMinLength = 0,
  reasonMaxLength = 250,
  reasonOptions,
  otherOptionValue = 'other',

  data,

  onConfirm,
  onCancel,
  triggerClassName = '',
}: CommonConfirmModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [reasonError, setReasonError] = useState('');

  const icons = {
    question: <MdQuestionMark className="text-primary" size={32} />,
    warning: <MdWarning className="text-warning" size={32} />,
    danger: <MdDangerous className="text-danger" size={32} />,
    info: <MdInfo className="text-blue-500" size={32} />,
  };

  const iconBgColors = {
    question: 'bg-primary/20',
    warning: 'bg-warning/20',
    danger: 'bg-danger/20',
    info: 'bg-blue-500/20',
  };

  const validateReason = () => {
    if (reasonOptions && reasonOptions.length > 0) {
      if (reasonRequired && !reason) {
        setReasonError('Please select a reason');
        return false;
      }

      if (reason === otherOptionValue) {
        const cleanedOther = otherReason.trim().replace(/\s+/g, ' ');

        if (reasonRequired && !cleanedOther) {
          setReasonError('Please provide a reason');
          return false;
        }

        if (cleanedOther.length > reasonMaxLength) {
          setReasonError(`Reason cannot exceed ${reasonMaxLength} characters`);
          return false;
        }
      }

      setReasonError('');
      return true;
    }

    if (reasonRequired && !reason.trim()) {
      setReasonError('Reason is required');
      return false;
    }

    if (reason.trim() && reason.length < reasonMinLength) {
      setReasonError(`Reason must be at least ${reasonMinLength} characters`);
      return false;
    }
    setReasonError('');
    return true;
  };

  const handleConfirmClick = () => {
    if (requireReason && !showReasonInput) {
      setShowReasonInput(true);
    } else {
      handleFinalConfirm();
    }
  };

  const handleFinalConfirm = async () => {
    if (requireReason && !validateReason()) {
      return;
    }

    setIsConfirming(true);
    try {
      if (onConfirm) {
        let note: string | undefined;

        if (reasonOptions && reasonOptions.length > 0) {
          note =
            reason === otherOptionValue
              ? otherReason.trim() || undefined
              : (reasonOptions.find((option) => option.value === reason)
                  ?.label ?? reason);
        } else {
          note = (reason || '').trim() || undefined;
        }

        await onConfirm(note, data);
      }
      handleClose();
    } catch (error) {
      console.error('Confirmation error:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    handleClose();
  };

  const handleClose = () => {
    setReason('');
    setOtherReason('');
    setShowReasonInput(false);
    setReasonError('');
    setIsConfirming(false);
    onClose();
  };

  return (
    <>
      <CustomButton
        isIconOnly={isIcon}
        label={buttonLabel}
        startContent={isIcon && (buttonIcon || <AiOutlineDelete size={18} />)}
        color={buttonColor === 'danger' ? 'default' : buttonColor}
        variant={buttonColor === 'danger' ? 'bordered' : buttonVariant}
        isLoading={isLoading}
        isDisabled={isDisabled}
        size="sm"
        className={
          buttonColor === 'danger'
            ? `text-xs border text-red-500 hover:bg-red-500 hover:border-red-500 hover:text-black transition-colors duration-300 ${triggerClassName}`
            : triggerClassName
        }
        onPress={onOpen}
      />

      {/* Step 1: Warning Confirmation & Provider Target Selector */}
      <Modal
        isOpen={isOpen && !showReasonInput}
        onOpenChange={(open) => !open && handleClose()}
        hideCloseButton
        size="md"
      >
        <ModalContent>
          <ModalBody>
            <div className="flex flex-col items-center justify-center space-y-4 py-4">
              <div
                className={`${iconBgColors[iconType]} p-3 rounded-full flex items-center justify-center`}
              >
                {icons[iconType]}
              </div>

              <p className="text-xl font-semibold text-gray-900 text-center">
                {title}
              </p>

              <p className="text-body text-center text-gray-600 text-xs">
                {description}
              </p>

              {/* Step 1 Broadcast Provider Selectors */}
              {providerSelectorOptions &&
                providerSelectorOptions.length > 0 && (
                  <div className="w-full mt-3 space-y-2">
                    <p className="text-xs font-bold text-gray-700">
                      Select Target Request:
                    </p>
                    <RadioGroup
                      size="sm"
                      value={selectedProviderValue}
                      onValueChange={(val) =>
                        onProviderChange && onProviderChange(val)
                      }
                    >
                      {providerSelectorOptions.map((opt) => (
                        <Radio
                          key={opt.value}
                          value={opt.value}
                          className="text-xs"
                        >
                          <span
                            className={
                              opt.value === 'ALL'
                                ? 'font-bold text-rose-600'
                                : 'text-gray-800'
                            }
                          >
                            {opt.label}
                          </span>
                        </Radio>
                      ))}
                    </RadioGroup>
                  </div>
                )}
            </div>
          </ModalBody>

          <ModalFooter className="flex justify-center items-center gap-3 p-4">
            <CustomButton
              label={cancelBtnText}
              color="default"
              variant="flat"
              className="w-full"
              size="sm"
              onPress={handleCancel}
              isDisabled={isConfirming}
            />

            <CustomButton
              label={requireReason ? 'Next' : confirmBtnText}
              color={confirmBtnColor}
              className="w-full"
              onPress={handleConfirmClick}
              isLoading={isConfirming && !requireReason}
              isDisabled={isConfirming}
              size="sm"
            />
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Step 2: Reason Selection Input Modal */}
      {requireReason && (
        <Modal
          isOpen={showReasonInput}
          onOpenChange={(open) => !open && setShowReasonInput(false)}
          hideCloseButton
          size="xl"
        >
          <ModalContent>
            <ModalBody>
              <div className="flex flex-col space-y-4 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {reasonLabel}
                  {reasonRequired && (
                    <span className="text-danger ml-1">*</span>
                  )}
                </h3>
                {reasonDescription && (
                  <p className="text-gray-600 text-sm">{reasonDescription}</p>
                )}
                {reasonOptions && reasonOptions.length > 0 ? (
                  <>
                    <div className="flex justify-start items-center">
                      <RadioGroup
                        size="sm"
                        value={reason}
                        onValueChange={(value) => {
                          setReason(value);
                          setReasonError('');
                          if (value !== otherOptionValue) {
                            setOtherReason('');
                          }
                        }}
                      >
                        {reasonOptions.map((option) => (
                          <Radio
                            key={option.value}
                            value={option.value}
                            className="text-sm"
                          >
                            {option.label}
                          </Radio>
                        ))}
                      </RadioGroup>
                    </div>

                    {reason === otherOptionValue && (
                      <div className="mt-1">
                        <CustomTextArea
                          label="Tell us more"
                          placeholder={reasonPlaceholder}
                          value={otherReason}
                          onValueChange={(value) => {
                            setOtherReason(value);
                            setReasonError('');
                          }}
                          maxLength={reasonMaxLength}
                          description={`${otherReason.length}/${reasonMaxLength} characters`}
                          minRows={3}
                          maxRows={5}
                        />
                      </div>
                    )}

                    {reasonError && (
                      <p className="text-danger text-sm">{reasonError}</p>
                    )}
                  </>
                ) : (
                  <Textarea
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      setReasonError('');
                    }}
                    placeholder={reasonPlaceholder}
                    minRows={4}
                    maxRows={8}
                    variant="bordered"
                    maxLength={reasonMaxLength}
                    isInvalid={!!reasonError}
                    errorMessage={reasonError}
                    description={`${reason.length}/${reasonMaxLength} characters`}
                  />
                )}
              </div>
            </ModalBody>

            <ModalFooter className="flex justify-center items-center gap-3 p-4">
              <CustomButton
                label="Back"
                color="default"
                variant="flat"
                className="w-full"
                onPress={() => setShowReasonInput(false)}
                isDisabled={isConfirming}
                size="sm"
              />

              <CustomButton
                label="Confirm"
                color={confirmBtnColor}
                className="w-full"
                onPress={handleFinalConfirm}
                isLoading={isConfirming}
                isDisabled={isConfirming}
                size="sm"
              />
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default CommonConfirmModal;
